import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import {
  Search, X, RefreshCw, UserCheck, Flame, Thermometer, Snowflake,
  Phone, Mail, MapPin, CheckSquare, Square, Users, Upload,
  Clock, ChevronRight, Loader2, Eye, AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { marketingAPI } from '../../utils/api';

const PRIORITY_CONFIG = {
  hot:  { label: 'Hot',  icon: Flame,       cls: 'bg-rose-100 text-rose-700 border-rose-200',   bar: 'bg-rose-400',   dot: 'bg-rose-500' },
  warm: { label: 'Warm', icon: Thermometer, cls: 'bg-amber-100 text-amber-700 border-amber-200', bar: 'bg-amber-400',  dot: 'bg-amber-500' },
  cold: { label: 'Cold', icon: Snowflake,   cls: 'bg-sky-100 text-sky-700 border-sky-200',       bar: 'bg-sky-400',    dot: 'bg-sky-500' },
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

// ─── Assign Modal ─────────────────────────────────────────────────────────────
function AssignModal({ count, consultants, onConfirm, onCancel }) {
  const [consultantId, setConsultantId] = useState('');
  const selected = consultants.find((c) => c.id === consultantId);
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Assign {count} Lead{count !== 1 ? 's' : ''}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Select Consultant</Label>
            <Select value={consultantId} onValueChange={setConsultantId}>
              <SelectTrigger><SelectValue placeholder="Choose a consultant..." /></SelectTrigger>
              <SelectContent>
                {consultants.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="font-medium">{c.full_name}</span>
                    <span className="text-xs text-slate-400 ml-2">{c.active_leads} active</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selected && (
            <div className="bg-teal-50 rounded-lg p-3 text-sm">
              <p className="font-semibold text-teal-800">{selected.full_name}</p>
              <p className="text-xs text-teal-600 mt-0.5">{selected.active_leads || 0} active leads currently</p>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button disabled={!consultantId} onClick={() => onConfirm(consultantId)} className="bg-teal-600 hover:bg-teal-700">
            <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Lead Row ─────────────────────────────────────────────────────────────────
function LeadRow({ lead, isSelected, onSelect, onAssign, onView }) {
  const pc = PRIORITY_CONFIG[lead.priority] || PRIORITY_CONFIG.warm;
  const PIcon = pc.icon;
  const score = lead.lead_score || 0;
  const scoreColor = score >= 70 ? 'text-emerald-600' : score >= 40 ? 'text-amber-600' : 'text-slate-400';

  return (
    <div className={`flex items-center gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${isSelected ? 'bg-teal-50' : ''}`}>
      {/* Checkbox */}
      <div
        className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${isSelected ? 'bg-teal-600 border-teal-600' : 'border-slate-300 hover:border-teal-400'}`}
        onClick={() => onSelect(lead.id)}
      >
        {isSelected && <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>}
      </div>

      {/* Priority bar */}
      <div className={`w-1 h-10 rounded-full flex-shrink-0 ${pc.bar}`} />

      {/* Avatar */}
      <div className="w-9 h-9 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
        {getInitials(lead.full_name)}
      </div>

      {/* Name + contact */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-800 text-sm truncate">{lead.full_name}</span>
          <Badge className={`text-xs px-1.5 py-0 h-4 border flex items-center gap-0.5 ${pc.cls}`}>
            <PIcon className="w-2.5 h-2.5" /> {pc.label}
          </Badge>
          {lead.is_duplicate && (
            <Badge className="text-xs px-1.5 py-0 h-4 bg-amber-100 text-amber-700 border border-amber-200">×{lead.enquiry_number || '2'}</Badge>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500 flex-wrap">
          {lead.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" />{lead.phone}</span>}
          {lead.email && <span className="flex items-center gap-0.5 hidden sm:flex"><Mail className="w-3 h-3" />{lead.email}</span>}
          {lead.country_of_interest && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{lead.country_of_interest}</span>}
        </div>
      </div>

      {/* Product */}
      {(lead.product_interest || lead.visa_type) && (
        <div className="text-xs text-slate-500 hidden md:block max-w-[120px] truncate">
          {lead.product_interest || lead.visa_type}
        </div>
      )}

      {/* Score */}
      <div className={`text-xs font-mono font-bold ${scoreColor} hidden sm:block w-8 text-center`}>
        {score}
      </div>

      {/* Imported date */}
      <div className="text-xs text-slate-400 hidden lg:block w-24 text-right">
        {formatDate(lead.created_at)}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="View lead" onClick={() => onView(lead.id)}>
          <Eye className="w-3.5 h-3.5 text-slate-400" />
        </Button>
        <Button size="sm" className="h-7 px-2.5 text-xs bg-teal-600 hover:bg-teal-700 gap-1" onClick={() => onAssign([lead.id])}>
          <UserCheck className="w-3 h-3" /> Assign
        </Button>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function LeadPool() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ hot: 0, warm: 0, cold: 0 });
  const [loading, setLoading] = useState(true);
  const [consultants, setConsultants] = useState([]);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [assignTarget, setAssignTarget] = useState(null); // null | 'bulk' | [leadId]
  const [assigning, setAssigning] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (priorityFilter) params.priority = priorityFilter;
      const { data } = await marketingAPI.getLeadPool(params);
      setLeads(data.leads || []);
      setTotal(data.total || 0);
      setStats(data.stats || { hot: 0, warm: 0, cold: 0 });
    } catch { toast.error('Failed to load lead pool'); }
    finally { setLoading(false); }
  }, [search, priorityFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    marketingAPI.getConsultants()
      .then(({ data }) => setConsultants(data.consultants || []))
      .catch(() => {});
  }, []);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === leads.length) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };

  const handleAssignConfirm = async (consultantId) => {
    const ids = assignTarget === 'bulk' ? [...selected] : assignTarget;
    setAssigning(true);
    try {
      const { data } = await marketingAPI.assignFromPool(ids, consultantId);
      toast.success(`${data.count} leads assigned successfully`);
      setSelected(new Set());
      setAssignTarget(null);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const allSelected = leads.length > 0 && selected.size === leads.length;
  const someSelected = selected.size > 0;

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content-bare overflow-y-auto bg-slate-50">
      <div className="p-6 space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Lead Pool</h1>
            <p className="text-sm text-slate-500">Unassigned leads waiting to be distributed to consultants</p>
          </div>
          <Button onClick={() => navigate('/admin/marketing/upload')} className="bg-teal-600 hover:bg-teal-700 gap-1.5">
            <Upload className="w-4 h-4" /> Import More Leads
          </Button>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Unassigned', value: total, color: 'bg-indigo-600', textColor: 'text-white' },
            { label: '🔥 Hot', value: stats.hot, color: 'bg-rose-100', textColor: 'text-rose-700' },
            { label: '🌡 Warm', value: stats.warm, color: 'bg-amber-100', textColor: 'text-amber-700' },
            { label: '❄️ Cold', value: stats.cold, color: 'bg-sky-100', textColor: 'text-sky-700' },
          ].map(({ label, value, color, textColor }) => (
            <div key={label} className={`${color} rounded-xl p-6 flex items-center gap-3`}>
              <div>
                <p className={`text-3xl font-bold ${textColor}`}>{value}</p>
                <p className={`text-xs ${textColor} opacity-80 mt-0.5`}>{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Empty pool state */}
        {!loading && total === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Pool is empty</h3>
              <p className="text-sm text-slate-500 mb-4">No unassigned leads. Import a CSV or all leads have been assigned.</p>
              <Button onClick={() => navigate('/admin/marketing/upload')} className="bg-teal-600 hover:bg-teal-700 gap-1.5">
                <Upload className="w-4 h-4" /> Import Leads
              </Button>
            </CardContent>
          </Card>
        )}

        {(total > 0 || loading) && (
          <Card>
            {/* Toolbar */}
            <div className="flex items-center gap-3 p-4 border-b border-slate-200 flex-wrap">
              {/* Select all checkbox */}
              <div
                className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${allSelected ? 'bg-teal-600 border-teal-600' : someSelected ? 'bg-teal-200 border-teal-400' : 'border-slate-300 hover:border-teal-400'}`}
                onClick={toggleAll}
              >
                {allSelected && <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>}
                {someSelected && !allSelected && <div className="w-2 h-0.5 bg-teal-600" />}
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  className="pl-9 w-56"
                  placeholder="Search name, phone, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Priority filter */}
              <Select value={priorityFilter || 'all'} onValueChange={v => setPriorityFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-36"><SelectValue placeholder="All priorities" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  <SelectItem value="hot">🔥 Hot</SelectItem>
                  <SelectItem value="warm">🌡 Warm</SelectItem>
                  <SelectItem value="cold">❄️ Cold</SelectItem>
                </SelectContent>
              </Select>

              {(search || priorityFilter) && (
                <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setPriorityFilter(''); }} className="gap-1 text-slate-500">
                  <X className="w-3.5 h-3.5" /> Clear
                </Button>
              )}

              <div className="ml-auto flex items-center gap-2">
                {someSelected && (
                  <Button size="sm" className="bg-teal-600 hover:bg-teal-700 gap-1.5" onClick={() => setAssignTarget('bulk')}>
                    <UserCheck className="w-3.5 h-3.5" /> Assign {selected.size} Selected
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={fetchLeads} className="gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            {/* Column headers */}
            <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500">
              <div className="w-4 flex-shrink-0" />
              <div className="w-1 flex-shrink-0" />
              <div className="w-9 flex-shrink-0" />
              <div className="flex-1">Lead</div>
              <div className="hidden md:block w-28">Product / Visa</div>
              <div className="hidden sm:block w-8 text-center">Score</div>
              <div className="hidden lg:block w-24 text-right">Imported</div>
              <div className="w-28 text-right">Action</div>
            </div>

            {/* Lead rows */}
            {loading ? (
              <div className="divide-y divide-slate-100">
                {Array(8).fill(0).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
                    <div className="w-4 h-4 bg-slate-200 rounded" />
                    <div className="w-9 h-9 bg-slate-200 rounded-full" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 bg-slate-200 rounded w-40" />
                      <div className="h-2.5 bg-slate-100 rounded w-56" />
                    </div>
                  </div>
                ))}
              </div>
            ) : leads.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No leads match your filters</p>
              </div>
            ) : (
              <div>
                {leads.map((lead) => (
                  <LeadRow
                    key={lead.id}
                    lead={lead}
                    isSelected={selected.has(lead.id)}
                    onSelect={toggleSelect}
                    onAssign={(ids) => setAssignTarget(ids)}
                    onView={(id) => navigate(`/sales/leads/${id}`)}
                  />
                ))}
              </div>
            )}

            {/* Footer pagination hint */}
            {leads.length > 0 && (
              <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-400 flex items-center justify-between">
                <span>Showing {leads.length} of {total} unassigned leads</span>
                {total > leads.length && (
                  <span className="text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Use search to find specific leads
                  </span>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Consultant workload sidebar card */}
        {consultants.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-500" /> Consultant Workload
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {consultants.map((c) => (
                  <div key={c.id} className="bg-slate-50 rounded-xl p-3 text-center">
                    <div className="w-9 h-9 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs font-bold mx-auto mb-1.5">
                      {getInitials(c.full_name)}
                    </div>
                    <p className="text-xs font-semibold text-slate-700 truncate">{c.full_name.split(' ')[0]}</p>
                    <p className="text-lg font-bold text-teal-600 mt-0.5">{c.active_leads}</p>
                    <p className="text-xs text-slate-400">active leads</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Assign Modal */}
      {assignTarget && (
        <AssignModal
          count={assignTarget === 'bulk' ? selected.size : 1}
          consultants={consultants}
          onConfirm={handleAssignConfirm}
          onCancel={() => setAssignTarget(null)}
        />
      )}

      {/* Floating bulk bar */}
      {someSelected && !assignTarget && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4">
          <CheckSquare className="w-4 h-4 text-teal-400" />
          <span className="font-semibold">{selected.size} selected</span>
          <div className="w-px h-5 bg-slate-700" />
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 gap-1.5 h-7" onClick={() => setAssignTarget('bulk')}>
            <UserCheck className="w-3.5 h-3.5" /> Assign to Consultant
          </Button>
          <Button size="sm" variant="ghost" className="text-slate-400 hover:bg-slate-700 h-7" onClick={() => setSelected(new Set())}>
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
      </div>
    </div>
  );
}
